import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { ArrowUp, CircleNotch, Microphone, Paperclip, Square } from '@phosphor-icons/react';
import { formatBytes } from '../lib/format';
import { useSpeechRecognition } from '../lib/voice';
import { AttachmentRow, fileExtension } from './AttachmentRow';

/** Matches the server's own ceiling on a question. */
const MAX_MESSAGE_CHARS = 4_000;
/** The counter is noise until the limit is close enough to matter. */
const COUNTER_THRESHOLD = Math.floor(MAX_MESSAGE_CHARS * 0.8);

export type ComposerAction =
  | 'microphone'
  | 'send'
  | 'stop'
  | 'stop-recording'
  | 'submitting'
  | 'stopping'
  | 'disabled'
  | 'idle';

/**
 * One button, one meaning at a time. Kept as a pure function because the order
 * of these branches is the whole behaviour: recording outranks everything, an
 * answer in flight outranks new input, and only an empty box offers the
 * microphone.
 */
export function resolveComposerAction(input: {
  hasContent: boolean;
  isResponding: boolean;
  isRecording: boolean;
}): ComposerAction {
  if (input.isRecording) return 'stop-recording';
  if (input.isResponding) return 'stop';
  if (input.hasContent) return 'send';
  return 'microphone';
}

export interface UploadPolicy {
  /** Extensions the importer can actually read, without dots. */
  acceptedExtensions: ReadonlySet<string>;
  maxFiles: number;
  /**
   * Ceiling on the whole upload, not on one file. The server applies
   * `FINOJAN_MAX_UPLOAD_BYTES` to the entire request body, so a per-file reading
   * of it accepted three 15MB files here and then lost all three to one 413.
   */
  maxTotalBytes: number;
}

export function acceptAttribute(policy: UploadPolicy): string {
  return [...policy.acceptedExtensions].map((extension) => `.${extension}`).join(',');
}

interface ChatComposerProps {
  /** An answer is in flight; the terminal button offers to stop it. */
  isResponding: boolean;
  isStopping?: boolean;
  disabled?: boolean;
  /** Why it is disabled, in Persian. Shown rather than left to guesswork. */
  disabledReason?: string;
  onSend: (text: string, files: File[]) => Promise<void> | void;
  onStop: () => void;
  placeholder?: string;
  /**
   * Omit to hide the attachment control entirely. It is hidden rather than
   * disabled when this workspace cannot import, because a button that can never
   * do anything is worse than no button.
   */
  uploadPolicy?: UploadPolicy;
}

let attachmentSequence = 0;

function rejectionFor(file: File, policy: UploadPolicy, bytesSoFar: number): string | null {
  if (!policy.acceptedExtensions.has(fileExtension(file.name))) {
    const formats = [...policy.acceptedExtensions].map((item) => item.toLocaleUpperCase('en-US')).join('، ');
    return `«${file.name}» از قالب‌های پشتیبانی‌شده نیست. قالب‌های مجاز: ${formats}`;
  }
  if (file.size > policy.maxTotalBytes) {
    return `«${file.name}» به‌تنهایی از سقف ${formatBytes(policy.maxTotalBytes)} بزرگ‌تر است.`;
  }
  // Measured against what is already attached, because the server weighs the
  // request as a whole and refuses it as a whole.
  if (bytesSoFar + file.size > policy.maxTotalBytes) {
    return `مجموع فایل‌ها باید حداکثر ${formatBytes(policy.maxTotalBytes)} باشد؛ «${file.name}» از این سقف عبور می‌کرد.`;
  }
  return null;
}

/**
 * Thrown by `onSend` when part of the work landed and part did not, so the
 * composer knows what to keep. Without it, a send that uploaded the files and
 * then failed on the question would hand both back — and the next attempt would
 * import the same ledger twice.
 */
export class ComposerRetry extends Error {
  constructor(message: string, readonly keep: { text: boolean; files: boolean }) {
    super(message);
    this.name = 'ComposerRetry';
  }
}

function visibleError(error: unknown): string {
  const detail = error instanceof Error ? error.message.trim() : '';
  // The server writes Persian for every failure it names; anything else is an
  // internal string that would only confuse the reader.
  return /[؀-ۿ]/u.test(detail)
    ? detail
    : 'ارسال انجام نشد. متن و فایل‌ها حفظ شدند؛ دوباره تلاش کنید.';
}

export function ChatComposer({
  isResponding,
  isStopping = false,
  disabled = false,
  disabledReason,
  onSend,
  onStop,
  placeholder = 'مثلاً: چرا سود خالص نسبت به دوره قبل کم شده است؟',
  uploadPolicy,
}: ChatComposerProps) {
  const [value, setValue] = useState('');
  const [attachments, setAttachments] = useState<Array<{ id: string; file: File }>>([]);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A ref as well as state: two fast Enter presses both read the old state
  // before either render lands, and the question would be asked twice.
  const submittingRef = useRef(false);
  const errorId = useId();

  const voice = useSpeechRecognition((transcript) => {
    setValue((current) => (current.trim() ? `${current.trim()} ${transcript}` : transcript));
    requestAnimationFrame(() => textareaRef.current?.focus());
  });

  const hasContent = value.trim().length > 0 || attachments.length > 0;
  const resolved = resolveComposerAction({ hasContent, isResponding, isRecording: voice.isRecording });
  const action: ComposerAction = isStopping
    ? 'stopping'
    : isSubmitting
      ? 'submitting'
      : disabled
        ? 'disabled'
        : !voice.supported && resolved === 'microphone'
          ? 'idle'
          : resolved;

  // Grow with the text instead of scrolling inside one line, up to the point
  // where the conversation above would start to disappear.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 168)}px`;
  }, [value]);

  const addFiles = useCallback((incoming: File[]) => {
    if (!incoming.length || !uploadPolicy) return;
    setAttachments((previous) => {
      const next = previous.slice();
      // Identity is name+size+mtime: the same file picked twice is one
      // attachment, but two genuinely different files sharing a name are two.
      const known = new Set(previous.map(({ file }) => `${file.name}:${file.size}:${file.lastModified}`));
      let bytesSoFar = previous.reduce((total, item) => total + item.file.size, 0);
      let rejection: string | null = null;

      for (const file of incoming) {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) continue;
        const problem = rejectionFor(file, uploadPolicy, bytesSoFar);
        if (problem) {
          rejection ??= problem;
          continue;
        }
        if (next.length >= uploadPolicy.maxFiles) {
          rejection ??= `در هر نوبت حداکثر ${uploadPolicy.maxFiles.toLocaleString('fa-IR')} فایل می‌توانید بفرستید.`;
          continue;
        }
        known.add(key);
        bytesSoFar += file.size;
        next.push({ id: `attachment-${++attachmentSequence}`, file });
      }

      setComposerError(rejection);
      return next;
    });
  }, [uploadPolicy]);

  const send = useCallback(async () => {
    if (disabled || !hasContent || isResponding || voice.isRecording || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setComposerError(null);
    const text = value.trim();
    const files = attachments.map((attachment) => attachment.file);
    try {
      await onSend(text, files);
      // Cleared only on success, so a failed send never loses what was typed.
      setValue('');
      setAttachments([]);
    } catch (error) {
      setComposerError(visibleError(error));
      // A partial failure says which half survived; anything else is total, and
      // everything stays put for another try.
      if (error instanceof ComposerRetry) {
        if (!error.keep.text) setValue('');
        if (!error.keep.files) setAttachments([]);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [attachments, disabled, hasContent, isResponding, onSend, value, voice.isRecording]);

  const triggerAction = useCallback(() => {
    if (action === 'send') return void send();
    if (action === 'stop') return onStop();
    if (action === 'stop-recording') return voice.stop();
    if (action === 'microphone') return voice.start();
    return undefined;
  }, [action, onStop, send, voice]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift/Ctrl/Cmd+Enter writes a newline, and `isComposing` keeps an IME
    // candidate selection from being read as "send".
    if (
      event.key === 'Enter'
      && !event.shiftKey
      && !event.metaKey
      && !event.ctrlKey
      && !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      void send();
    }
  };

  // A drag only counts when it carries files, so selecting text inside the page
  // and dragging it keeps behaving like text rather than arming an upload.
  const carriesFiles = (transfer: DataTransfer | null) =>
    Array.from(transfer?.types ?? []).includes('Files');

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !uploadPolicy || !carriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));
    // Reset so picking the same file again still fires a change event.
    event.target.value = '';
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (disabled || !uploadPolicy) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) return;
    event.preventDefault();
    addFiles(files);
  };

  const presentation = useMemo(() => {
    if (action === 'send') return { label: 'ارسال پرسش', icon: <ArrowUp size={17} weight="bold" />, tone: 'primary' };
    if (action === 'stop') return { label: 'توقف پاسخ', icon: <Square size={14} weight="fill" />, tone: 'danger' };
    if (action === 'stop-recording') return { label: 'پایان گفتن', icon: <Square size={14} weight="fill" />, tone: 'danger' };
    if (action === 'stopping') return { label: 'در حال توقف', icon: <CircleNotch className="spin" size={17} />, tone: 'danger' };
    if (action === 'submitting') return { label: attachments.length ? 'در حال فرستادن فایل‌ها' : 'در حال ارسال', icon: <CircleNotch className="spin" size={17} />, tone: 'primary' };
    if (action === 'disabled') return { label: disabledReason ?? 'گفتگو در دسترس نیست', icon: <ArrowUp size={17} weight="bold" />, tone: 'muted' };
    if (action === 'idle') return { label: 'برای ارسال، پرسشی بنویسید', icon: <ArrowUp size={17} weight="bold" />, tone: 'muted' };
    return { label: 'گفتن پرسش با صدا', icon: <Microphone size={17} />, tone: 'primary' };
  }, [action, attachments.length, disabledReason]);

  const inert = action === 'submitting' || action === 'stopping' || action === 'disabled' || action === 'idle';
  const message = composerError ?? voice.error;

  return (
    <div
      className={`chat-composer${isDragging ? ' is-dragging' : ''}`}
      aria-busy={isResponding || isStopping || isSubmitting}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={(event) => {
        // Ignore the leave events fired while crossing between children.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsDragging(false);
      }}
      onDrop={(event) => {
        if (disabled || !uploadPolicy || !carriesFiles(event.dataTransfer)) return;
        event.preventDefault();
        setIsDragging(false);
        addFiles(Array.from(event.dataTransfer.files ?? []));
      }}
    >
      {uploadPolicy && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptAttribute(uploadPolicy)}
          className="visually-hidden"
          tabIndex={-1}
          onChange={handleFiles}
        />
      )}

      {isDragging && <div className="composer-drop-hint">فایل‌های دفتر کل را همین‌جا رها کنید</div>}

      {attachments.length > 0 && (
        <ul className="composer-attachments" aria-label="فایل‌های پیوست‌شده">
          {attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              name={attachment.file.name}
              sizeBytes={attachment.file.size}
              mimeType={attachment.file.type}
              file={attachment.file}
              onRemove={() => setAttachments((items) => items.filter((item) => item.id !== attachment.id))}
            />
          ))}
        </ul>
      )}

      <textarea
        ref={textareaRef}
        className="composer-input"
        value={value}
        rows={1}
        dir="rtl"
        lang="fa"
        aria-label="پرسش شما"
        aria-describedby={message ? errorId : undefined}
        placeholder={placeholder}
        maxLength={MAX_MESSAGE_CHARS}
        disabled={disabled}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />

      <div className="composer-actions">
        <div className="composer-actions__start">
          {uploadPolicy && (
            <button
              type="button"
              className="composer-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isSubmitting}
              // Labelled as well as captioned: the caption is hidden on narrow
              // screens, where the button would otherwise be an unnamed icon.
              aria-label="افزودن فایل"
              title="فایل اکسل، متن جدولی یا PDF"
            >
              <Paperclip size={17} />
              <span>افزودن فایل</span>
            </button>
          )}
        </div>

        <div className="composer-actions__end">
          {voice.isRecording && <span className="composer-recording"><i />در حال شنیدن</span>}
          {value.length >= COUNTER_THRESHOLD && (
            <span className="composer-count">
              <bdi dir="ltr">{value.length.toLocaleString('fa-IR')}</bdi> از {MAX_MESSAGE_CHARS.toLocaleString('fa-IR')}
            </span>
          )}
          <button
            type="button"
            className={`composer-terminal composer-terminal--${presentation.tone}`}
            data-composer-action={action}
            aria-label={presentation.label}
            title={presentation.label}
            disabled={inert}
            onClick={triggerAction}
          >
            {presentation.icon}
          </button>
        </div>
      </div>

      {message && <p className="composer-error" id={errorId} role="alert">{message}</p>}
      {!message && disabled && disabledReason && (
        <p className="composer-disabled-reason" role="status">{disabledReason}</p>
      )}
    </div>
  );
}
